import { styled } from "@mui/material/styles";
import { InsertChart, Tune, FormatPaint } from "@mui/icons-material";
import { forwardRef } from "react";
import { MenuType } from "reducers/chartMenuReducer";
import { withTransientProps } from "core/utils/transientProps";

export interface GraphConfigBarProps {
  /** Which menu button is currently selected, defaults to none */
  selectedMenu?: MenuType;
  /**
   * Callback which is fired when one of the menu buttons is clicked
   * @param clickedItem The key of the button clicked
   */
  onClickMenuButton?: (clickedMenuType: MenuType) => void;
}

/** The vertical menu bar for configuring a graph */
export const GraphConfigBar = forwardRef<HTMLDivElement, GraphConfigBarProps>(
  function GraphConfigBar(
    { selectedMenu = MenuType.NONE, onClickMenuButton = () => {} },
    ref
  ) {
    const chartTypePressed = selectedMenu === MenuType.CHART_TYPE;
    const configurePressed = selectedMenu === MenuType.CONFIGURE;
    const editPressed = selectedMenu === MenuType.EDIT;

    return (
      <BackgroundContainer ref={ref}>
        <SelectableButton
          aria-label="Chart type menu"
          title="Chart type"
          aria-pressed={chartTypePressed}
          $selected={chartTypePressed}
          onClick={() => onClickMenuButton(MenuType.CHART_TYPE)}
        >
          <InsertChart color="inherit" />
        </SelectableButton>
        <Divider />
        <SelectableButton
          aria-label="Configure menu"
          title="Configure"
          aria-pressed={configurePressed}
          $selected={configurePressed}
          onClick={() => onClickMenuButton(MenuType.CONFIGURE)}
        >
          <Tune color="inherit" />
        </SelectableButton>
        <Divider />
        <SelectableButton
          aria-label="Edit menu"
          title="Edit"
          aria-pressed={editPressed}
          $selected={editPressed}
          onClick={() => onClickMenuButton(MenuType.EDIT)}
        >
          <FormatPaint color="inherit" />
        </SelectableButton>
      </BackgroundContainer>
    );
  }
);

const BackgroundContainer = styled("div")`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.palette.secondary.main};
  width: 51px;
  height: 171px;
  border-radius: 0 5px 5px 0;
  align-items: center;
  justify-content: center;
  gap: 8px;

  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
`;

const SelectableButton = styled("button", withTransientProps)<{
  $selected: boolean;
}>`
  display: flex;
  padding: ${({ theme }) => theme.spacing(1)};
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  border: none;
  color: ${({ theme }) => theme.palette.secondary.contrastText};
  cursor: pointer;

  background-color: ${props =>
    props.$selected ? `rgba(102, 100, 164, 0.49)` : "unset"};

  :hover {
    background-color: rgba(102, 100, 164, 0.49);
  }
`;

const Divider = styled("hr")`
  width: 100%;
  margin: 0;
  height: 1px;
  border: none;
  background-color: rgba(255, 255, 255, 0.17);
`;
